import { useCallback, useEffect, useRef, useState } from 'react';
import { Track, VideoPresets } from 'livekit-client';

// How much video this client sends, as a user-facing choice.
//
// What arrives is already handled automatically: adaptiveStream (see
// useVoiceConnection) drops incoming layers for tiles that are small or
// off-screen, which is better than anything a person could pick by hand. What
// it cannot know is the sender's situation - a tethered phone, a laptop fan
// at full tilt, a conference wifi shared by thirty people - so that is the
// end this exposes.
//
// Applied with restartTrack rather than by republishing: it renegotiates the
// capture constraints on the existing track, so the publication, its
// subscribers and any processor attached to it all survive the change.

const CAPTURE_PRESETS = {
    high: VideoPresets.h720,
    balanced: VideoPresets.h540,
    low: VideoPresets.h180
};

// The simulcast ladder published alongside each capture size. Always at least
// one rung below the capture, so a viewer with a thumbnail-sized tile has
// something cheap to receive - that is what adaptiveStream selects from.
const SIMULCAST_LAYERS = {
    high: [VideoPresets.h180, VideoPresets.h360],
    balanced: [VideoPresets.h180, VideoPresets.h360],
    low: []
};

export const VIDEO_QUALITIES = ['high', 'balanced', 'low'];
const DEFAULT_QUALITY = 'balanced';
const STORAGE_KEY = 'meetingVideoQuality';

function storedQuality() {
    const value = localStorage.getItem(STORAGE_KEY);
    return VIDEO_QUALITIES.includes(value) ? value : DEFAULT_QUALITY;
}

export function useVideoQuality(room, cameraOn) {
    const [quality, setQuality] = useState(storedQuality);
    const [applying, setApplying] = useState(false);
    const qualityRef = useRef(quality);
    qualityRef.current = quality;

    const apply = useCallback(async (next) => {
        if (!room) return;
        const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const track = publication && publication.track;
        if (!track) return;
        await track.restartTrack({
            resolution: CAPTURE_PRESETS[next].resolution,
            // Carried through because restartTrack replaces the capture
            // constraints wholesale; omitting these would silently drop back
            // to the browser's defaults.
            facingMode: 'user'
        });
    }, [room]);

    const selectQuality = useCallback(async (next) => {
        if (!VIDEO_QUALITIES.includes(next)) return;
        setQuality(next);
        localStorage.setItem(STORAGE_KEY, next);
        setApplying(true);
        try {
            await apply(next);
        } catch (error) {
            // A camera that refuses the requested size keeps the one it has,
            // which is a worse picture than asked for rather than no picture.
        } finally {
            setApplying(false);
        }
    }, [apply]);

    // A camera turned on after the choice was made has to inherit it - the
    // track that existed when the user picked is gone by then.
    useEffect(() => {
        if (!room || !cameraOn) return undefined;
        if (qualityRef.current === DEFAULT_QUALITY) return undefined;
        let cancelled = false;
        const timer = setTimeout(() => {
            if (!cancelled) apply(qualityRef.current).catch(() => {});
        }, 200);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [room, cameraOn, apply]);

    return { quality, selectQuality, applying, simulcastLayers: SIMULCAST_LAYERS[quality] };
}
